import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import AuthService from '../services/AuthService';
import DashboardRealtimeService from '../services/DashboardRealtimeApiService';

const formatDate = (date) => format(new Date(date), 'M/d');

const getDailyValues = (hourData, key) => {
  const results = [];
  const lastHourCountryData = new Map();
  for (let hour = 0; hour < 24; hour++) {
    const data = hourData.filter(item => item.hour === hour);

    data.forEach(item => {
      const value = item[key] || 0;
      if (item.country && value > 0) {
        lastHourCountryData.set(item.country, value);
      }
    });

    const total = Array.from(lastHourCountryData.values()).reduce((acc, value) => acc + value, 0);
    results.push(total);
  }
  return results;
}

const aggregateHourData = (realtimeData, key) => {
  if (!realtimeData) return [['Hour', 'Day Before Yesterday', 'Yesterday', 'Today']];

  const data = [[
    'Hour', 
    formatDate(realtimeData.dayBeforeYesterday.date),
    formatDate(realtimeData.yesterday.date),
    formatDate(realtimeData.today.date)
  ]];

  const values = [
    getDailyValues(realtimeData.dayBeforeYesterday.hourData, key),
    getDailyValues(realtimeData.yesterday.hourData, key),
    getDailyValues(realtimeData.today.hourData, key),
  ]

  for (let hour = 0; hour < 24; hour++) {
    data.push([`${hour}`, values[0][hour], values[1][hour], values[2][hour]]);
  }

  return data;
}


const sumHourData = (data, key) => {
  return data.reduce((acc, item) => {
    const value = item[key] || 0;
    return acc + value;
  }, 0);
};

const mergeCountryData = (hourData, key) => {
  const data = [];
  const countryData = {};
  hourData
    .filter(item => item.country)
    .forEach(item => {
      if (item.country in countryData) {
        if (item[key] > countryData[item.country]) {
          countryData[item.country] = item[key];
        }
      }
      else {
        countryData[item.country] = item[key];
      }
    });

  Object.entries(countryData).forEach(([country, value]) => {
    if (country && value > 0) {
      data.push([country, value]);
    }
  });
  return data;
}

const useDashboardRealtimeViewModel = (selectedLicense) => {
  const [realtimeData, setRealtimeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async (selectedLicense) => {
      try {
        setIsLoading(true);
        setError(null);
        if (!selectedLicense || "license_id" in selectedLicense === false) {
          throw new Error('No license selected');
        }
        const data = await DashboardRealtimeService.getRealtimeData(selectedLicense);
        setRealtimeData(data);

      } catch (err) {
        setError(err.message || 'Failed to fetch realtime data');
        AuthService.logoutWhenError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData(selectedLicense);
  }, [selectedLicense]);

  const registerNumByHour = useMemo(() => {
    return aggregateHourData(realtimeData, 'num_licenses_new_used');
  }, [realtimeData]);

  const registerNumByCountry = useMemo(() => {
    if (!realtimeData) return [['Country', 'Registrations']];

    const data = [[
      'Country',
      format(new Date( realtimeData.dayBeforeYesterday.date), 'M/d'),
      format(new Date( realtimeData.yesterday.date), 'M/d'),
      format(new Date( realtimeData.today.date), 'M/d')
    ]];

    const countryData1 = mergeCountryData(realtimeData.dayBeforeYesterday.hourData, 'num_licenses_new_used');
    const countryData2 = mergeCountryData(realtimeData.yesterday.hourData, 'num_licenses_new_used');
    const countryData3 = mergeCountryData(realtimeData.today.hourData, 'num_licenses_new_used');

    const countryMap = new Map();
    [...countryData1, ...countryData2, ...countryData3].forEach(([country]) => {
      if (!countryMap.has(country)) {
        countryMap.set(country, [0, 0, 0]);
      }
    });

    countryData1.forEach(([country, value]) => {
      countryMap.get(country)[0] = value;
    });
    countryData2.forEach(([country, value]) => {
      countryMap.get(country)[1] = value;
    });
    countryData3.forEach(([country, value]) => {
      countryMap.get(country)[2] = value;
    });

    countryMap.forEach((values, country) => {
      data.push([country, ...values]);
    });

    data.sort((a, b) => (b[1] + b[2] + b[3]) - (a[1] + a[2] + a[3]));
    if (data.length === 1) {
      data.push(['No Data', 0, 0, 0]);
    }
    return data;
  }, [realtimeData]);

  const usageByHour = useMemo(() => {
    return aggregateHourData(realtimeData, 'usage_total');
  }, [realtimeData]);

  const usageByCountry = useMemo(() => {
    if (!realtimeData) return [['Country', 'Usage']];
    const data = [[
      'Country',
      format(new Date( realtimeData.dayBeforeYesterday.date), 'M/d'),
      format(new Date( realtimeData.yesterday.date), 'M/d'),
      format(new Date( realtimeData.today.date), 'M/d')
    ]];

    const countryData1 = mergeCountryData(realtimeData.dayBeforeYesterday.hourData, 'usage_total');
    const countryData2 = mergeCountryData(realtimeData.yesterday.hourData, 'usage_total');
    const countryData3 = mergeCountryData(realtimeData.today.hourData, 'usage_total');

    const countryMap = new Map();
    [...countryData1, ...countryData2, ...countryData3].forEach(([country]) => {
      if (!countryMap.has(country)) {
        countryMap.set(country, [0, 0, 0]);
      }
    });

    countryData1.forEach(([country, value]) => {
      countryMap.get(country)[0] = value;
    });
    countryData2.forEach(([country, value]) => {
      countryMap.get(country)[1] = value;
    });
    countryData3.forEach(([country, value]) => {
      countryMap.get(country)[2] = value;
    });

    countryMap.forEach((values, country) => {
      data.push([country, ...values]);
    });

    data.sort((a, b) => (b[1] + b[2] + b[3]) - (a[1] + a[2] + a[3]));
    if (data.length === 1) {
      data.push(['No Data', 0, 0, 0]);
    }
    return data;
  }, [realtimeData]);

  return {
    isLoading,
    error,
    registerNumByHour,
    registerNumByCountry,
    usageByHour,
    usageByCountry,
  };
};

export default useDashboardRealtimeViewModel;